import React, { useState, useContext, useEffect } from "react";
import axios from "axios";
import Select from "react-select";
import {
    Button,
    Col,
    Form,
    Modal,
    Row,
    Card,
    Spinner,
    Tooltip,
    OverlayTrigger,
    Tab,
    Nav
} from "react-bootstrap";
import {
    faExclamationCircle,
    faTabletScreenButton
} from "@fortawesome/free-solid-svg-icons";
import NotyfContext from "../../contexts/NotyfContext.js";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
    HelpTooltip, handleShare, redirectURL
} from "../../assets/functions.js";
import DropzoneComponent from "../Dropzone.js";
import { domainConfig } from "../../config.js";
import PopoverPicker from "../PopoverPicker.js";
import { BackgroundFormComponent } from '../../components/Applications'
import { getActivityDisplayName } from "./ActivityTableUtils.js";

const getPlatform2ActivityIds = (source = {}) => {
    const configuredIds = Array.isArray(source.activity_ids) && source.activity_ids.length > 0
        ? source.activity_ids
        : [source.activity_id];

    return Array.from(new Set(
        configuredIds
            .map((activityId) => Number(activityId))
            .filter((activityId) => Number.isInteger(activityId) && activityId > 0)
    ));
};

const Gallery = ({
    fonts,
    handleChange,
    values,
    setFieldValue,
    errors,
    touched,
    handlePreviewSuccess,
    handlePreviewError,
    dataTable,
    setDataAddForm,
    dataAddForm,
    setModal,
    applications
}) => {
    const notyf = useContext(NotyfContext);
    const [activeTab, setActiveTab] = useState('default');
    const [props, setProps] = useState({
        backgroundColor: values.json.apresentation?.textSide?.background.color,
        textColor: values.json.apresentation?.textSide?.textColor
    });
    const [testingPlatform2Source, setTestingPlatform2Source] = useState(false);
    const [platform2TestPhotos, setPlatform2TestPhotos] = useState([]);
    const [showPlatform2TestResults, setShowPlatform2TestResults] = useState(false);
    const [fetchingOtherEventPhotos, setFetchingOtherEventPhotos] = useState(false);
    const [platform2Activities, setPlatform2Activities] = useState([]);
    const [testingPlatform1Source, setTestingPlatform1Source] = useState(false);
    const [platform1TestPhotos, setPlatform1TestPhotos] = useState([]);
    const [showPlatform1TestResults, setShowPlatform1TestResults] = useState(false);
    const [platform1Activities, setPlatform1Activities] = useState([]);

    const platform1PhotoSource = values.json.platform1PhotoboothSource || {};
    const platform2PhotoSource = values.json.platform2PhotoActivitySource || {};
    const photoSourceType = platform1PhotoSource.enabled === true ? 'platform1' : 'platform2';
    const otherEventPhotoSource = photoSourceType === 'platform1'
        ? platform1PhotoSource
        : platform2PhotoSource;
    const otherEventPhotosEnabled = platform1PhotoSource.enabled === true || platform2PhotoSource.enabled === true;

    const applyOtherEventPhotoSource = (eventToken, response, resetSelection = true) => {
        const sourceType = response.data?.source;
        const activities = Array.isArray(response.data?.activities) ? response.data.activities : [];

        if (sourceType === 'platform1') {
            setFieldValue('json.platform1PhotoboothSource.enabled', true);
            setFieldValue('json.platform1PhotoboothSource.event_token', eventToken);
            setFieldValue('json.platform1PhotoboothSource.event_id', response.data.event_id || '');
            setFieldValue('json.platform1PhotoboothSource.event_name', response.data.event_name || '');
            if (resetSelection) {
                setFieldValue('json.platform1PhotoboothSource.customizacao_id', '');
                setFieldValue('json.platform1PhotoboothSource.customization_name', '');
            }
            setFieldValue('json.platform2PhotoActivitySource.enabled', false);
            setPlatform1Activities(activities);
            setPlatform2Activities([]);
            return;
        }

        if (sourceType === 'platform2') {
            setFieldValue('json.platform2PhotoActivitySource.enabled', true);
            setFieldValue('json.platform2PhotoActivitySource.event_token', eventToken);
            setFieldValue('json.platform2PhotoActivitySource.event_id', response.data.event_id || '');
            setFieldValue('json.platform2PhotoActivitySource.event_name', response.data.event_name || '');
            if (resetSelection) {
                setFieldValue('json.platform2PhotoActivitySource.activity_ids', []);
                setFieldValue('json.platform2PhotoActivitySource.activity_id', '');
            }
            setFieldValue('json.platform1PhotoboothSource.enabled', false);
            setPlatform2Activities(activities);
            setPlatform1Activities([]);
        }
    };

    // On mount, reload the selected source through the cross-platform token lookup.
    useEffect(() => {
        const eventToken = String(otherEventPhotoSource.event_token || '').trim();
        if (!otherEventPhotosEnabled || !/^[A-Za-z0-9]{6}$/.test(eventToken) || !otherEventPhotoSource.event_id) return;

        axios.get('/api/event/photo-sources-by-token', {
            params: { event_token: eventToken },
        }).then((response) => {
            const activities = Array.isArray(response.data?.activities) ? response.data.activities : [];
            if (response.data?.source === photoSourceType) {
                if (photoSourceType === 'platform1') setPlatform1Activities(activities);
                else setPlatform2Activities(activities);
                return;
            }
            applyOtherEventPhotoSource(eventToken, response, false);
        }).catch(() => { });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const integrateOtherEventPhotos = async () => {
        const eventToken = String(otherEventPhotoSource.event_token || '').trim();
        if (!/^[A-Za-z0-9]{6}$/.test(eventToken)) {
            notyf.open({ type: 'danger', message: 'Informe um token de evento válido de 6 caracteres.', dismissible: true });
            return;
        }

        setFetchingOtherEventPhotos(true);
        try {
            const response = await axios.get('/api/event/photo-sources-by-token', {
                params: { event_token: eventToken },
            });
            const activities = Array.isArray(response.data?.activities) ? response.data.activities : [];
            if (!activities.length) {
                notyf.open({ type: 'warning', message: 'Nenhuma fonte de fotos foi encontrada para este evento.', dismissible: true });
                return;
            }

            applyOtherEventPhotoSource(eventToken, response);
            const sourceLabel = response.data.source === 'platform1' ? 'Photobooth(s)' : 'atividade(s)';
            notyf.open({ type: 'success', message: `${activities.length} ${sourceLabel} encontrada(s).`, dismissible: true });
        } catch (error) {
            notyf.open({ type: 'danger', message: error.response?.data?.message || 'Não foi possível carregar a fonte de fotos.', dismissible: true });
        } finally {
            setFetchingOtherEventPhotos(false);
        }
    };

    const removeOtherEventPhotoSource = () => {
        setFieldValue('json.platform1PhotoboothSource.enabled', false);
        setFieldValue('json.platform1PhotoboothSource.event_token', '');
        setFieldValue('json.platform1PhotoboothSource.event_id', '');
        setFieldValue('json.platform1PhotoboothSource.event_name', '');
        setFieldValue('json.platform1PhotoboothSource.customizacao_id', '');
        setFieldValue('json.platform1PhotoboothSource.customization_name', '');
        setFieldValue('json.platform2PhotoActivitySource.enabled', false);
        setFieldValue('json.platform2PhotoActivitySource.event_token', '');
        setFieldValue('json.platform2PhotoActivitySource.event_id', '');
        setFieldValue('json.platform2PhotoActivitySource.event_name', '');
        setFieldValue('json.platform2PhotoActivitySource.activity_ids', []);
        setFieldValue('json.platform2PhotoActivitySource.activity_id', '');
        setPlatform1Activities([]);
        setPlatform2Activities([]);
        setPlatform1TestPhotos([]);
        setPlatform2TestPhotos([]);
        setShowPlatform1TestResults(false);
        setShowPlatform2TestResults(false);
    };

    const testPlatform2PhotoSource = async () => {
        const source = values.json.platform2PhotoActivitySource || {};
        const eventToken = String(source.event_token || '').trim();
        const activityIds = getPlatform2ActivityIds(source);
        if (!/^[A-Za-z0-9]{6}$/.test(eventToken) || activityIds.length === 0) {
            notyf.open({ type: 'danger', message: 'Selecione ao menos uma atividade válida para testar.', dismissible: true });
            return;
        }
        setTestingPlatform2Source(true);
        try {
            const responses = await Promise.all(activityIds.map((activityId) => axios.get('/api/event/plataformaEventos/approved-activity-photos/test', {
                params: { event_token: eventToken, activity_id: activityId },
            })));
            const photos = Array.from(new Map(
                responses.flatMap((response, responseIndex) => {
                    const activityId = Number(response.data?.activity_id) || activityIds[responseIndex];
                    return (Array.isArray(response.data?.photos) ? response.data.photos : [])
                        .map((photo) => ({ ...photo, activity_id: activityId }));
                }).map((photo) => [
                    `${photo.activity_id}:${photo.submission_id || photo.path}`,
                    photo,
                ])
            ).values());
            setPlatform2TestPhotos(photos);
            setShowPlatform2TestResults(true);
            notyf.open({ type: 'success', message: `${photos.length} foto(s) aprovada(s) encontrada(s).`, dismissible: true });
        } catch (error) {
            notyf.open({ type: 'danger', message: error.response?.data?.message || 'Não foi possível testar a fonte de fotos.', dismissible: true });
        } finally {
            setTestingPlatform2Source(false);
        }
    };

    const testPlatform1PhotoboothSource = async () => {
        const source = values.json.platform1PhotoboothSource || {};
        const eventToken = String(source.event_token || '').trim();
        const customizationId = Number(source.customizacao_id);
        if (!/^[A-Za-z0-9]{6}$/.test(eventToken) || !Number.isInteger(customizationId) || customizationId <= 0) {
            notyf.open({ type: 'danger', message: 'Selecione um Photobooth v\u00e1lido para testar.', dismissible: true });
            return;
        }

        setTestingPlatform1Source(true);
        try {
            const response = await axios.get('/api/event/webapps/photobooth-media/test', {
                params: { event_token: eventToken, customizacao_id: customizationId },
            });
            const photos = Array.isArray(response.data?.photos) ? response.data.photos : [];
            setPlatform1TestPhotos(photos);
            setShowPlatform1TestResults(true);
            notyf.open({ type: 'success', message: `${photos.length} foto(s) encontrada(s).`, dismissible: true });
        } catch (error) {
            notyf.open({ type: 'danger', message: error.response?.data?.message || 'N\u00e3o foi poss\u00edvel testar a fonte do Photobooth.', dismissible: true });
        } finally {
            setTestingPlatform1Source(false);
        }
    };

    return (
        <>
            <Form.Group as={Col} className="textHeaderAplicativo mb-3">
                <h5 className="card-title mb-0">Definições</h5>
            </Form.Group>
            <Row className="mx-0 mb-3">
                <Form.Group
                    className="mb-3"
                    as={Col}
                    sm="12">
                    <Form.Label>Título</Form.Label>
                    <Form.Control
                        type="text"
                        name="titulo"
                        value={values.titulo}
                        onChange={handleChange}
                        isInvalid={!!errors.titulo}
                        isValid={touched.titulo && !errors.titulo}
                    />
                    <Form.Control.Feedback type="valid"></Form.Control.Feedback>
                    <Form.Control.Feedback type="invalid">
                        {errors.titulo}
                    </Form.Control.Feedback>
                </Form.Group>
                <div className="d-flex text-start">
                    <Form.Group sm={6} className="mb-3">
                        <Form.Check
                            className="mode"
                            checked={values.json.useMemzo.status ? true : false}
                            type="checkbox"
                            name={"json.useMemzo.status"}
                            onChange={(e) => {
                                setFieldValue(
                                    'json.useMemzo.status',
                                    values.json.useMemzo.status === true ? false : true
                                );
                            }}
                            disabled
                            label={<>Memzo AI <OverlayTrigger overlay={<Tooltip>Serviço de AI de reconhecimento facial. Permite aos participantes acessarem suas fotos</Tooltip>}><HelpTooltip icon={faExclamationCircle} /></OverlayTrigger></>}
                            isInvalid={!!errors.json?.useMemzo?.status}
                            feedback={errors.json?.useMemzo?.status}
                            feedbackType="invalid">
                        </Form.Check>
                    </Form.Group>
                </div>
                <Row className="text-start">
                    {values.json.useMemzo.status &&
                        <Form.Group as={Col} md={6} className='mb-3'>
                            <Form.Label>Link da pasta - Google Drive <OverlayTrigger overlay={<Tooltip>Compartilhe o folder com o nosso email de serviço para liberar o upload de imagens</Tooltip>}><HelpTooltip icon={faExclamationCircle} /></OverlayTrigger><span className="badge btn-primary ms-2 pointer" onClick={() => handleShare(
                                'flex-plataforma-de-aplicativos@plataforma-de-aplicativos.iam.gserviceaccount.com',
                                notyf,
                            )}>Copiar email</span>
                            </Form.Label>
                            <Form.Control
                                type="text"
                                name="json.useMemzo.value"
                                value={values.json.useMemzo.value}
                                onChange={(e) => {
                                    handleChange(e);
                                }}
                                isInvalid={!!errors.json?.useMemzo?.value}
                            />
                            <Form.Control.Feedback type="valid"></Form.Control.Feedback>
                            <Form.Control.Feedback type="invalid">
                                {errors.json?.useMemzo?.value}
                            </Form.Control.Feedback>
                        </Form.Group>
                    }
                    <Form.Group as={Col} sm={12}>
                        <Form.Check
                            type="checkbox"
                            name="json.platform2PhotoActivitySource.enabled"
                            label={<>Exibir fotos de outro evento <OverlayTrigger overlay={<Tooltip>{'Informe o token do evento que contém as fotos. A fonte será identificada automaticamente entre as plataformas.'}</Tooltip>}><HelpTooltip icon={faExclamationCircle} /></OverlayTrigger></>}
                            checked={otherEventPhotosEnabled}
                            onChange={(event) => {
                                const restorePlatform1Source = event.target.checked && Boolean(
                                    platform1PhotoSource.event_token || platform1PhotoSource.event_id,
                                );
                                setFieldValue('json.platform1PhotoboothSource.enabled', restorePlatform1Source);
                                setFieldValue('json.platform2PhotoActivitySource.enabled', event.target.checked && !restorePlatform1Source);
                            }}
                        />
                    </Form.Group>
                    {values.json.platform2PhotoActivitySource?.enabled === true && (() => {
                        const src = values.json.platform2PhotoActivitySource || {};
                        const isIntegrated = !!(src.event_id && src.event_token);
                        const selectedActivityIds = getPlatform2ActivityIds(src);
                        const hasActivity = selectedActivityIds.length > 0;
                        const activityOptions = platform2Activities.map((act) => ({
                            value: Number(act.id),
                            label: act.title || `Atividade #${act.id}`,
                            subtitle: getActivityDisplayName(act),
                        }));
                        const selectedActivityOptions = activityOptions.filter((option) => selectedActivityIds.includes(option.value));
                        return (
                            <>
                                <Form.Group as={Col} md={6} className="mb-3">
                                    <Form.Label>
                                        Token do evento
                                        {isIntegrated ? (
                                            <Button
                                                type="button"
                                                variant="danger"
                                                className="btn-sm ms-2"
                                                onClick={removeOtherEventPhotoSource}
                                            >
                                                Remover
                                            </Button>
                                        ) : (
                                            <Button
                                                type="button"
                                                variant="primary"
                                                className="btn-sm ms-2"
                                                onClick={integrateOtherEventPhotos}
                                                disabled={fetchingOtherEventPhotos}
                                            >
                                                {fetchingOtherEventPhotos ? <Spinner animation="border" size="sm" className="me-1" /> : null}
                                                Integrar
                                            </Button>
                                        )}
                                    </Form.Label>
                                    <Form.Control
                                        type="text"
                                        maxLength={6}
                                        name="json.platform2PhotoActivitySource.event_token"
                                        value={src.event_token || ''}
                                        onChange={handleChange}
                                        disabled={isIntegrated}
                                        isInvalid={!!errors.json?.platform2PhotoActivitySource?.event_token}
                                        isValid={touched.json?.platform2PhotoActivitySource?.event_token && !errors.json?.platform2PhotoActivitySource?.event_token}
                                    />
                                    <Form.Control.Feedback type="invalid">
                                        {errors.json?.platform2PhotoActivitySource?.event_token}
                                    </Form.Control.Feedback>
                                </Form.Group>
                                {isIntegrated && src.event_name && (
                                    <Form.Group as={Col} md={6} className="mb-3">
                                        <Form.Label>Evento</Form.Label>
                                        <Form.Control
                                            type="text"
                                            disabled
                                            value={src.event_name}
                                        />
                                    </Form.Group>
                                )}
                                {isIntegrated && platform2Activities.length > 0 && (
                                    <Form.Group as={Col} md={12} className="mb-3">
                                        <Form.Label>
                                            Atividades
                                            {hasActivity && (
                                                <Button
                                                    type="button"
                                                    variant="primary"
                                                    className="btn-sm ms-2"
                                                    onClick={testPlatform2PhotoSource}
                                                    disabled={testingPlatform2Source}
                                                >
                                                    {testingPlatform2Source ? <Spinner animation="border" size="sm" className="me-1" /> : null}
                                                    Testar
                                                </Button>
                                            )}
                                        </Form.Label>
                                        <Select
                                            isMulti
                                            name="json.platform2PhotoActivitySource.activity_ids"
                                            options={activityOptions}
                                            value={selectedActivityOptions}
                                            onChange={(selectedOptions) => {
                                                const activityIds = (selectedOptions || [])
                                                    .map((option) => Number(option.value))
                                                    .filter((activityId) => Number.isInteger(activityId) && activityId > 0);
                                                setFieldValue('json.platform2PhotoActivitySource.activity_ids', activityIds);
                                                setFieldValue('json.platform2PhotoActivitySource.activity_id', activityIds[0] || '');
                                            }}
                                            placeholder="Selecione as atividades..."
                                            className="react-select-container"
                                            classNamePrefix="react-select"
                                            closeMenuOnSelect={false}
                                            isSearchable
                                            formatOptionLabel={(option) => (
                                                <div>
                                                    <div>{option.label}</div>
                                                    {option.subtitle && <small className="text-muted">{option.subtitle}</small>}
                                                </div>
                                            )}
                                            noOptionsMessage={() => 'Nenhuma atividade encontrada'}
                                        />
                                        {(errors.json?.platform2PhotoActivitySource?.activity_ids || errors.json?.platform2PhotoActivitySource?.activity_id) && (
                                            <div className="invalid-feedback d-block">
                                                {errors.json?.platform2PhotoActivitySource?.activity_ids || errors.json?.platform2PhotoActivitySource?.activity_id}
                                            </div>
                                        )}
                                    </Form.Group>
                                )}
                            </>
                        );
                    })()}
                    {values.json.platform1PhotoboothSource?.enabled === true && (() => {
                        const src = values.json.platform1PhotoboothSource || {};
                        const isIntegrated = !!(src.event_id && src.event_token);
                        const customizationId = Number(src.customizacao_id);
                        const hasPhotobooth = Number.isInteger(customizationId) && customizationId > 0;
                        const photoboothOptions = platform1Activities.map((activity) => ({
                            value: Number(activity.customizacao_id),
                            label: activity.titulo || `Photobooth #${activity.customizacao_id}`,
                            subtitle: activity.application_name || 'Photobooth',
                        }));
                        const selectedPhotobooth = photoboothOptions.find((option) => option.value === customizationId) || null;
                        return (
                            <>
                                <Form.Group as={Col} md={6} className="mb-3">
                                    <Form.Label>
                                        Token do evento
                                        {isIntegrated ? (
                                            <Button
                                                type="button"
                                                variant="danger"
                                                className="btn-sm ms-2"
                                                onClick={removeOtherEventPhotoSource}
                                            >
                                                Remover
                                            </Button>
                                        ) : (
                                            <Button
                                                type="button"
                                                variant="primary"
                                                className="btn-sm ms-2"
                                                onClick={integrateOtherEventPhotos}
                                                disabled={fetchingOtherEventPhotos}
                                            >
                                                {fetchingOtherEventPhotos ? <Spinner animation="border" size="sm" className="me-1" /> : null}
                                                Integrar
                                            </Button>
                                        )}
                                    </Form.Label>
                                    <Form.Control
                                        type="text"
                                        maxLength={6}
                                        name="json.platform1PhotoboothSource.event_token"
                                        value={src.event_token || ''}
                                        onChange={handleChange}
                                        disabled={isIntegrated}
                                        isInvalid={!!errors.json?.platform1PhotoboothSource?.event_token}
                                        isValid={touched.json?.platform1PhotoboothSource?.event_token && !errors.json?.platform1PhotoboothSource?.event_token}
                                    />
                                    <Form.Control.Feedback type="invalid">
                                        {errors.json?.platform1PhotoboothSource?.event_token}
                                    </Form.Control.Feedback>
                                </Form.Group>
                                {isIntegrated && src.event_name && (
                                    <Form.Group as={Col} md={6} className="mb-3">
                                        <Form.Label>Evento</Form.Label>
                                        <Form.Control
                                            type="text"
                                            disabled
                                            value={src.event_name}
                                        />
                                    </Form.Group>
                                )}
                                {isIntegrated && platform1Activities.length > 0 && (
                                    <Form.Group as={Col} md={12} className="mb-3">
                                        <Form.Label>
                                            Photobooth
                                            {hasPhotobooth && (
                                                <Button
                                                    type="button"
                                                    variant="primary"
                                                    className="btn-sm ms-2"
                                                    onClick={testPlatform1PhotoboothSource}
                                                    disabled={testingPlatform1Source}
                                                >
                                                    {testingPlatform1Source ? <Spinner animation="border" size="sm" className="me-1" /> : null}
                                                    Testar
                                                </Button>
                                            )}
                                        </Form.Label>
                                        <Select
                                            name="json.platform1PhotoboothSource.customizacao_id"
                                            options={photoboothOptions}
                                            value={selectedPhotobooth}
                                            onChange={(selectedOption) => {
                                                setFieldValue('json.platform1PhotoboothSource.customizacao_id', selectedOption?.value || '');
                                                setFieldValue('json.platform1PhotoboothSource.customization_name', selectedOption?.label || '');
                                            }}
                                            placeholder="Selecione o Photobooth..."
                                            className="react-select-container"
                                            classNamePrefix="react-select"
                                            isSearchable
                                            formatOptionLabel={(option) => (
                                                <div>
                                                    <div>{option.label}</div>
                                                    {option.subtitle && <small className="text-muted">{option.subtitle}</small>}
                                                </div>
                                            )}
                                            noOptionsMessage={() => 'Nenhum Photobooth encontrado'}
                                        />
                                        {errors.json?.platform1PhotoboothSource?.customizacao_id && (
                                            <div className="invalid-feedback d-block">
                                                {errors.json.platform1PhotoboothSource.customizacao_id}
                                            </div>
                                        )}
                                    </Form.Group>
                                )}
                            </>
                        );
                    })()}
                </Row>
            </Row>
            <Modal show={showPlatform2TestResults} onHide={() => setShowPlatform2TestResults(false)} size="sm" centered>
                <Modal.Header closeButton>
                    <Modal.Title>Conteúdo disponível</Modal.Title>
                </Modal.Header>
                <Modal.Body className="text-center py-4">
                    <div className="display-3 fw-bold text-primary lh-1 mb-2">
                        {platform2TestPhotos.length}
                    </div>
                    <p className="mb-0 text-muted">
                        {platform2TestPhotos.length === 1
                            ? 'foto aprovada encontrada'
                            : 'fotos aprovadas encontradas'}
                    </p>
                </Modal.Body>
            </Modal>
            <Modal show={showPlatform1TestResults} onHide={() => setShowPlatform1TestResults(false)} size="sm" centered>
                <Modal.Header closeButton>
                    <Modal.Title>{'Conteúdo disponível'}</Modal.Title>
                </Modal.Header>
                <Modal.Body className="text-center py-4">
                    <div className="display-3 fw-bold text-primary lh-1 mb-2">
                        {platform1TestPhotos.length}
                    </div>
                    <p className="mb-0 text-muted">
                        {platform1TestPhotos.length === 1
                            ? 'foto do Photobooth encontrada'
                            : 'fotos do Photobooth encontradas'}
                    </p>
                </Modal.Body>
            </Modal>
            <Form.Group as={Col} className="textHeaderAplicativo mb-3">
                <h5 className="card-title mb-0">
                    Participante
                </h5>
            </Form.Group>
            <Row className="mx-0 mb-3">
                <Form.Group
                    as={Col}
                    md="6"
                    className="mx-0 mb-3">
                    <Form.Label>Tipo de exibição <OverlayTrigger overlay={<Tooltip>Usuário: Na tela final mostra a foto enviada pelo participante. Totem: Na tela final mostra o QRCode para visualizar a imagem</Tooltip>}><HelpTooltip icon={faExclamationCircle} /></OverlayTrigger></Form.Label>
                    <Form.Select
                        onChange={handleChange}
                        name="json.displayMode.value"
                        value={values.json.displayMode?.value}>
                        <option value={'user'}>
                            Usuário
                        </option>
                        <option value={'totem'}>
                            Totem
                        </option>
                    </Form.Select>
                </Form.Group>
                <div className="mb-3">
                    <div className="d-flex text-start">
                        <Form.Group sm={6}>
                            <Form.Check
                                checked={values.json.allowMultipleSubmissions?.status === true ? true : false}
                                type="checkbox"
                                name="json.allowMultipleSubmissions.status"
                                label="Permitir múltiplos envios por usuário"
                                onChange={(e) => {
                                    setFieldValue(
                                        'json.allowMultipleSubmissions.status',
                                        e.target.checked
                                    );
                                }}
                            />
                            <Form.Check
                                checked={values.json.allowFileUpload?.status !== false}
                                type="checkbox"
                                name="json.allowFileUpload.status"
                                label="Permitir upload de arquivo"
                                onChange={(e) => {
                                    setFieldValue(
                                        'json.allowFileUpload.status',
                                        e.target.checked
                                    );
                                }}
                            />
                        </Form.Group>
                    </div>
                </div>
            </Row>
            <Row className="mx-0 mb-3">
                <p className="text-start fw-bold mb-2"><FontAwesomeIcon
                    icon={faTabletScreenButton}
                    className="me-1"
                />Tela inicial</p>
                <Form.Group
                    className="mb-3"
                    as={Col}
                    sm="12"

                >
                    <Form.Label>Título</Form.Label>
                    <Form.Control
                        as="textarea"
                        name="json.title.value"
                        value={values.json.title.value}
                        onChange={(e) => {
                            if (e.target.value === '') {
                                setFieldValue("json.title.status", false);
                            } else {
                                setFieldValue("json.title.status", true);
                            }
                            setFieldValue("json.title.value", e.target.value);
                        }}
                        isInvalid={!!errors?.json?.title?.value}
                        isValid={touched?.json?.title?.value && !errors.json?.title?.value}
                    />
                    <Form.Control.Feedback type="valid"></Form.Control.Feedback>
                    <Form.Control.Feedback type="invalid">
                        {errors?.json?.title?.value}
                    </Form.Control.Feedback>
                </Form.Group>
                <Form.Group
                    className="mb-3"
                    as={Col}
                    sm="12"

                >
                    <Form.Label>Descrição</Form.Label>
                    <Form.Control
                        as="textarea"
                        name="json.description.value"
                        value={values.json.description.value}
                        onChange={(e) => {
                            if (e.target.value === '') {
                                setFieldValue("json.description.status", false);
                            } else {
                                setFieldValue("json.description.status", true);
                            }
                            setFieldValue("json.description.value", e.target.value);
                        }}
                        isInvalid={!!errors?.json?.description?.value}
                        isValid={touched?.json?.description?.value && !errors.json?.description?.value}
                    />
                    <Form.Control.Feedback type="valid"></Form.Control.Feedback>
                    <Form.Control.Feedback type="invalid">
                        {errors?.json?.description?.value}
                    </Form.Control.Feedback>
                </Form.Group>
            </Row>

            <Row className="mx-0 mb-3">
                <p className="text-start fw-bold mb-2"><FontAwesomeIcon
                    icon={faTabletScreenButton}
                    className="me-1"
                />Tela final</p>
                <Form.Group
                    className="mb-3"
                    as={Col}
                    sm="12"
                >
                    <Form.Label>Título</Form.Label>
                    <Form.Control
                        as="textarea"
                        name="json.endPage.title.value"
                        value={values.json.endPage?.title?.value || ''}
                        onChange={(e) => {
                            const nextValue = e.target.value;
                            setFieldValue("json.endPage.title.status", nextValue !== '');
                            setFieldValue("json.endPage.title.value", nextValue);
                        }}
                        isInvalid={!!errors?.json?.endPage?.title?.value}
                        isValid={touched?.json?.endPage?.title?.value && !errors?.json?.endPage?.title?.value}
                    />
                    <Form.Control.Feedback type="valid"></Form.Control.Feedback>
                    <Form.Control.Feedback type="invalid">
                        {errors?.json?.endPage?.title?.value}
                    </Form.Control.Feedback>
                </Form.Group>
                <Form.Group
                    className="mb-3"
                    as={Col}
                    sm="12"
                >
                    <Form.Label>Descrição</Form.Label>
                    <Form.Control
                        as="textarea"
                        name="json.endPage.description.value"
                        value={values.json.endPage?.description?.value || ''}
                        onChange={(e) => {
                            const nextValue = e.target.value;
                            setFieldValue("json.endPage.description.status", nextValue !== '');
                            setFieldValue("json.endPage.description.value", nextValue);
                        }}
                        isInvalid={!!errors?.json?.endPage?.description?.value}
                        isValid={touched?.json?.endPage?.description?.value && !errors?.json?.endPage?.description?.value}
                    />
                    <Form.Control.Feedback type="valid"></Form.Control.Feedback>
                    <Form.Control.Feedback type="invalid">
                        {errors?.json?.endPage?.description?.value}
                    </Form.Control.Feedback>
                </Form.Group>
            </Row>

            <Form.Group as={Col} className="textHeaderAplicativo mb-3">
                <h5 className="card-title mb-0">Modo apresentação</h5>
            </Form.Group>
            <Row className="mx-0">
                <Form.Group
                    className="mb-3"
                    as={Col}
                    sm="12"

                >
                    <Form.Label>Título</Form.Label>
                    <Form.Control
                        as="textarea"
                        name="json.apresentation.title.value"
                        value={values.json.apresentation.title.value}
                        onChange={(e) => {
                            if (e.target.value === '') {
                                setFieldValue("json.apresentation.title.status", false);
                            } else {
                                setFieldValue("json.apresentation.title.status", true);
                            }
                            setFieldValue("json.apresentation.title.value", e.target.value);
                        }}
                        isInvalid={!!errors?.json?.apresentation?.title?.value}
                        isValid={touched?.json?.apresentation?.title?.value && !errors?.json?.apresentation?.title?.value}
                    />
                    <Form.Control.Feedback type="valid"></Form.Control.Feedback>
                    <Form.Control.Feedback type="invalid">
                        {errors.json?.apresentation?.title?.value}
                    </Form.Control.Feedback>
                </Form.Group>
                <Form.Group
                    as={Col}
                    md={4}
                    className="mb-3"
                >
                    <Form.Label>Tamanho fonte (M) (rem)</Form.Label>
                    <Form.Control
                        type="number"
                        name="json.apresentation.textSide.h5"
                        step={"0.01"}
                        value={values.json.apresentation?.textSide?.h5}
                        onChange={handleChange}
                        isInvalid={!!errors.json?.apresentation?.textSide?.h5}
                        isValid={touched.json?.apresentation?.textSide?.h5 && !errors.json?.apresentation?.textSide?.h5}
                    />
                    <Form.Control.Feedback type="valid"></Form.Control.Feedback>
                    <Form.Control.Feedback type="invalid">
                        {errors.json?.apresentation?.textSide?.h5}
                    </Form.Control.Feedback>
                </Form.Group>
                <Form.Group
                    as={Col}
                    md={4}
                    className="mb-3"
                >
                    <Form.Label>Tamanho fonte (P) (rem)</Form.Label>
                    <Form.Control
                        type="number"
                        name="json.apresentation.textSide.p"
                        step={"0.01"}
                        value={values.json.apresentation?.textSide?.p}
                        onChange={handleChange}
                        isInvalid={!!errors.json?.apresentation?.textSide?.p}
                        isValid={touched.json?.apresentation?.textSide?.p && !errors.json?.apresentation?.textSide?.p}
                    />
                    <Form.Control.Feedback type="valid"></Form.Control.Feedback>
                    <Form.Control.Feedback type="invalid">
                        {errors.json?.apresentation?.textSide?.p}
                    </Form.Control.Feedback>
                </Form.Group>
                <div className="col-md-3 mb-3">
                    <Form.Label>Cor do texto</Form.Label>
                    <div className={`d-flex justify-content-start align-items-center ${errors?.json?.apresentation?.textSide?.textColor ? 'is-invalid' : ''}`}>
                        <Form.Control
                            type="hidden"
                            name="json.apresentation.textSide.textColor"
                            value={values.json?.apresentation?.textSide?.textColor}
                            isInvalid={!!errors?.json?.apresentation?.textSide?.textColor}
                            isValid={touched?.json?.apresentation?.textSide?.textColor && !errors?.json?.apresentation?.textSide?.textColor}
                        />
                        <PopoverPicker
                            color={props.textColor}
                            onChangeX={(e) => {
                                setFieldValue("json.apresentation.textSide.textColor", e);
                                setProps(prevState => ({
                                    ...prevState,
                                    textColor: e
                                }));
                            }}
                            classes={`me-1 picker ${errors?.json?.apresentation?.textSide?.textColor ? 'is-invalid' : ''}`}
                        />
                    </div>
                    <Form.Control.Feedback type="valid"></Form.Control.Feedback>
                    <Form.Control.Feedback type="invalid">
                        {errors?.json?.apresentation?.textSide?.textColor ? errors?.json?.apresentation?.textSide?.textColor : ''}
                    </Form.Control.Feedback>
                </div>
            </Row>
            <Row className="mx-0 mb-3">
                <Form.Group
                    as={Col}
                    md="6">
                    <Form.Label>Tipo de layout</Form.Label>
                    <Form.Select
                        onChange={(e) => {
                            // eslint-disable-next-line eqeqeq
                            if (e.target.value == 2) {
                                setFieldValue(
                                    "json.apresentation.imageSide.value", 40
                                );
                                setFieldValue(
                                    "json.apresentation.textSide.value", 60
                                );
                            } else {
                                setFieldValue(
                                    "json.apresentation.imageSide.value", 0
                                );
                                setFieldValue(
                                    "json.apresentation.textSide.value", 100
                                );
                            }
                        }}
                        value={values.json.apresentation?.imageSide?.value && values.json.apresentation?.imageSide?.value > 0 ? 2 : 1}>
                        <option value={1}>
                            Simples
                        </option>
                        <option value={2}>
                            Com banner
                        </option>
                    </Form.Select>
                </Form.Group>
            </Row>
            {
                values.json.apresentation.imageSide?.value > 0 &&
                <>
                    <Row className="mx-0 mb-3">
                        <Form.Group
                            as={Col}
                            md="3"
                            className="mb-3"
                        >
                            <Form.Label>Tamanho do banner (%)</Form.Label>
                            <Form.Control
                                type="number"
                                name="json.apresentation.imageSide.value"
                                value={values.json.apresentation?.imageSide?.value}
                                onChange={(e) => {
                                    if (e.target.value === undefined || e.target.value === '') {
                                        e.target.value = 0
                                    }
                                    let textSideValue = 100 - e.target.value
                                    setFieldValue("json.apresentation.imageSide.value", e.target.value);
                                    setFieldValue("json.apresentation.textSide.value", textSideValue);
                                }}
                                isInvalid={!!errors.json?.apresentation?.imageSide?.value}
                                isValid={touched.json?.apresentation?.imageSide?.value && !errors.json?.apresentation?.imageSide?.value}
                            />
                            <Form.Control.Feedback type="valid"></Form.Control.Feedback>
                            <Form.Control.Feedback type="invalid">
                                {errors.json?.apresentation?.imageSide?.value}
                            </Form.Control.Feedback>
                        </Form.Group>
                        <Form.Group
                            as={Col}
                            md="3"
                            className="mb-3"

                        >
                            <Form.Label>Posição</Form.Label>
                            <Form.Select
                                onChange={handleChange}
                                name="json.apresentation.imageSide.position"
                                value={values.json.apresentation?.imageSide?.position}>
                                <option value={'start'}>
                                    Esquerda
                                </option>
                                <option value={'end'}>
                                    Direita
                                </option>
                            </Form.Select>
                        </Form.Group>
                    </Row>
                    <Row className="mx-0 mb-3">
                        <Form.Group
                            as={Col}
                            md="6" className="mb-3">
                            <Card className="mb-3">
                                <Card.Body>
                                    <div className="text-center mb-3">
                                        <div className="d-flex justify-content-center mb-3">
                                            <Form.Label>Banner</Form.Label>
                                        </div>
                                    </div>
                                    <Form.Control
                                        type="hidden"
                                        name="json.apresentation.imageSide.background"
                                        value={values.json.apresentation?.imageSide?.background}
                                        onChange={handleChange}
                                        isInvalid={!!errors?.json?.apresentation?.imageSide?.background}
                                        isValid={touched?.json?.apresentation?.imageSide?.background && !errors?.json?.apresentation?.imageSide?.background}
                                    />
                                    <div className="imagesUpload">
                                        <img
                                            className={`h-100 img-thumbnail ${!!errors?.json?.apresentation?.imageSide?.background ? "is-invalid" : ""}`}
                                            src={
                                                values.json.apresentation?.imageSide?.background !== "" && values.json.apresentation?.imageSide?.background !== null
                                                    ? domainConfig.imageServer +
                                                    "/customizacao/" +
                                                    values.customizacao_id +
                                                    "/" +
                                                    values.json.apresentation?.imageSide?.background
                                                    : domainConfig.imageServer + "/empty-background.png"
                                            }
                                            alt="ImagemBase"
                                        />
                                        <Form.Control.Feedback type="valid"></Form.Control.Feedback>
                                        <Form.Control.Feedback type="invalid">
                                            {errors?.json?.apresentation?.imageSide?.background}
                                        </Form.Control.Feedback>
                                    </div>
                                    <DropzoneComponent
                                        className="mt-3 dropzoneComp"
                                        type="image"
                                        handlePreviewSuccess={handlePreviewSuccess}
                                        handlePreviewError={handlePreviewError}
                                        setFieldValue={setFieldValue}
                                        folder="customizacao"
                                        id={values.customizacao_id}
                                        field="json.apresentation.imageSide.background"
                                    />
                                </Card.Body>
                            </Card>
                        </Form.Group>
                    </Row>
                </>
            }

            <Row className="mx-0 mb-3">
                <Form.Group
                    as={Col}
                    md="6">
                    <Form.Label>Tipo de plano de fundo {values.json.apresentation.textSide.background.type === 'color' && <OverlayTrigger
                        overlay={<Tooltip><b>Exemplo: </b>linear-gradient(90deg,rgba(42, 123, 155, 1) 0%, rgba(87, 199, 133, 1) 50%, rgba(237, 221, 83, 1) 100%)</Tooltip>}
                    >
                        <span className="badge text-dark bg-light cursor-pointer ms-1" onClick={() => redirectURL(true, 'https://cssgradient.io/')}>CSS Gradient</span>
                    </OverlayTrigger>}</Form.Label>
                    <Form.Select
                        name="json.apresentation.textSide.background.type"
                        onChange={handleChange}
                        value={values.json.apresentation.textSide.background.type}
                        isInvalid={!!errors.json?.apresentation?.textSide?.background?.type}
                        isValid={touched.json?.apresentation?.textSide?.background?.type && !errors.json?.apresentation?.textSide?.background?.type}>
                        <option value={"color"}>
                            Cor
                        </option>
                        <option value={"image"}>
                            Imagem
                        </option>
                    </Form.Select>
                    <Form.Control.Feedback type="valid"></Form.Control.Feedback>
                    <Form.Control.Feedback type="invalid">{errors.json?.apresentation?.textSide?.background?.type}</Form.Control.Feedback>
                </Form.Group>
            </Row>
            {values.json?.apresentation?.textSide?.background?.type !== 'color' ? <Row className="mb-3 mx-0">
                <BackgroundFormComponent touched={touched} errors={errors} values={values} setFieldValue={setFieldValue} handlePreviewSuccess={handlePreviewSuccess} handlePreviewError={handlePreviewError}></BackgroundFormComponent>
            </Row> :
                <Row className="mx-0 mb-3">
                    <div className="col-md-3 mb-3">
                        <div className={`d-flex justify-content-start align-items-center ${errors?.json?.apresentation?.textSide?.background?.color ? 'is-invalid' : ''}`}>
                            <Form.Control
                                type="hidden"
                                name="json.apresentation.textSide.background.color"
                                value={values.json.apresentation.textSide.background.color}
                                isInvalid={!!errors?.json?.apresentation?.textSide?.background?.color}
                                isValid={touched?.json?.apresentation?.textSide?.background?.color && !errors?.json?.apresentation?.textSide?.background?.color}
                            />
                            <PopoverPicker
                                color={props.backgroundColor}
                                onChangeX={(e) => {
                                    setFieldValue("json.apresentation.textSide.background.color", e);
                                    setProps(prevState => ({
                                        ...prevState,
                                        backgroundColor: e
                                    }));
                                }}
                                classes={`me-1 picker ${errors?.json?.apresentation?.textSide?.background?.color ? 'is-invalid' : ''}`}
                            />
                        </div>
                        <Form.Control.Feedback type="valid"></Form.Control.Feedback>
                        <Form.Control.Feedback type="invalid">
                            {errors?.json?.apresentation?.textSide?.background?.color ? errors?.json?.apresentation?.textSide?.background?.color : ''}
                        </Form.Control.Feedback>
                    </div>
                </Row>
            }
            <Row className="mx-0 mb-3">
                <p className="text-start fw-bold mb-2">Logomarca</p>
                <Form.Group as={Col} sm={6} className='mb-3'>
                    <Form.Label>Largura da logomarca (px)</Form.Label>
                    <Form.Control
                        type="number"
                        name="json.apresentation.configs.logoSize"
                        value={values.json.apresentation.configs.logoSize}
                        onChange={(e) => {
                            handleChange(e);
                        }}
                        isInvalid={!!errors.json?.apresentation?.configs?.logoSize}
                    />
                    <Form.Control.Feedback type="valid"></Form.Control.Feedback>
                    <Form.Control.Feedback type="invalid">
                        {errors.json?.apresentation?.configs?.logoSize}
                    </Form.Control.Feedback>
                </Form.Group>
            </Row>
            <Row className="mx-0 mb-3">
                <Form.Group
                    className="mb-3"
                    as={Col}
                    sm="12"
                >
                    <Form.Check
                        checked={values.json.apresentation?.showQRCode.status === true ? true : false}
                        type="checkbox"
                        name="json.apresentation.showQRCode.status"
                        label="Mostrar QRCode"
                        onChange={(e) => {
                            setFieldValue(
                                "json.apresentation.showQRCode.status",
                                values.json.apresentation.showQRCode?.status === true ? false : true
                            );
                        }}
                    />
                    {values.json.apresentation?.showQRCode?.status === true ? (
                        <Row>
                            <Form.Group
                                className="mb-3"
                                as={Col}
                                sm="4"
                            >
                                <Form.Label>Texto</Form.Label>
                                <Form.Control
                                    type="text"
                                    name="json.apresentation.showQRCode.text"
                                    value={values.json.apresentation.showQRCode.text}
                                    onChange={handleChange}
                                    isInvalid={!!errors.json?.apresentation?.showQRCode?.text}
                                    isValid={
                                        touched.json?.apresentation?.showQRCode?.text &&
                                        !errors.json?.apresentation?.showQRCode?.text
                                    }
                                />
                                <Form.Control.Feedback type="valid"></Form.Control.Feedback>
                                <Form.Control.Feedback type="invalid">
                                    {errors.json?.apresentation?.showQRCode?.text}
                                </Form.Control.Feedback>
                            </Form.Group>
                            <Form.Group
                                className="mb-3"
                                as={Col}
                                sm="4"

                            >
                                <Form.Label>URL<OverlayTrigger overlay={<Tooltip>Caso nenhuma URL seja fornecida o QRCode irá redirecionar o usuário ao evento</Tooltip>}>
                                    <HelpTooltip icon={faExclamationCircle} />
                                </OverlayTrigger></Form.Label>
                                <Form.Control
                                    type="text"
                                    name="json.apresentation.showQRCode.link"
                                    value={values.json.apresentation.showQRCode.link}
                                    onChange={handleChange}
                                    isInvalid={!!errors.json?.apresentation?.showQRCode?.link}
                                    isValid={
                                        touched.json?.apresentation?.showQRCode?.link &&
                                        !errors.json?.apresentation?.showQRCode?.link
                                    }
                                />
                                <Form.Control.Feedback type="valid"></Form.Control.Feedback>
                                <Form.Control.Feedback type="invalid">
                                    {errors.json?.apresentation?.showQRCode?.link}
                                </Form.Control.Feedback>
                            </Form.Group>
                            <Form.Group
                                className="mb-3"
                                as={Col}
                                sm="4"

                            >
                                <Form.Label>Posição Y</Form.Label>
                                <Form.Select
                                    onChange={(e) => {
                                        setFieldValue("json.apresentation.showQRCode.position.y", e.target.value);
                                    }}
                                    value={values.json.apresentation.showQRCode.position.y}>
                                    <option value={'top'}>
                                        Topo
                                    </option>
                                    <option value={'bottom'}>
                                        Fundo
                                    </option>
                                </Form.Select>
                                <Form.Control.Feedback type="valid"></Form.Control.Feedback>
                                <Form.Control.Feedback type="invalid">
                                    {errors.json?.apresentation?.showQRCode?.position?.y}
                                </Form.Control.Feedback>
                            </Form.Group>
                            <Form.Group
                                as={Col}
                                sm="4">
                                <Form.Label>Posição X</Form.Label>
                                <Form.Select
                                    onChange={(e) => {
                                        setFieldValue("json.apresentation.showQRCode.position.x", e.target.value);
                                    }}
                                    value={values.json.apresentation.showQRCode.position.x}>
                                    <option value={'left'}>
                                        Esquerda
                                    </option>
                                    <option value={'right'}>
                                        Direita
                                    </option>
                                </Form.Select>
                                <Form.Control.Feedback type="valid"></Form.Control.Feedback>
                                <Form.Control.Feedback type="invalid">
                                    {errors.json?.apresentation?.showQRCode?.position?.x}
                                </Form.Control.Feedback>
                            </Form.Group>
                        </Row>
                    ) : (
                        ""
                    )}
                </Form.Group>
            </Row>
            <Row className="mx-0">
                <div className="tab">
                    <Tab.Container id="left-tabs-example" activeKey={activeTab} onSelect={(k) => setActiveTab(k)}>
                        <Nav variant="tabs" className="">
                            <Nav.Item>
                                <Nav.Link eventKey={'default'}>Galeria padrão</Nav.Link>
                            </Nav.Item>
                            <Nav.Item>
                                <Nav.Link eventKey={'overlay'}>Galeria com sobreposição</Nav.Link>
                            </Nav.Item>
                            <Nav.Item>
                                <Nav.Link eventKey={'photo'}>Captura de fotos</Nav.Link>
                            </Nav.Item>
                        </Nav>
                        <Tab.Content>
                            <Tab.Pane eventKey={'default'} className="mt-3">
                                <GalleryPresentation dataTable={dataTable} values={values} touched={touched} errors={errors} handleChange={handleChange} setFieldValue={setFieldValue} setDataAddForm={setDataAddForm} dataAddForm={dataAddForm} handlePreviewSuccess={handlePreviewSuccess} handlePreviewError={handlePreviewError} fonts={fonts} setModal={setModal} applications={applications}></GalleryPresentation>
                            </Tab.Pane>
                            <Tab.Pane eventKey={'overlay'} className="mt-3">
                                <GalleryPresentationOverlay dataTable={dataTable} values={values} touched={touched} errors={errors} handleChange={handleChange} setFieldValue={setFieldValue} setDataAddForm={setDataAddForm} dataAddForm={dataAddForm} handlePreviewSuccess={handlePreviewSuccess} handlePreviewError={handlePreviewError} fonts={fonts} setModal={setModal} applications={applications}></GalleryPresentationOverlay>
                            </Tab.Pane>
                            <Tab.Pane eventKey={'photo'} className="mt-3">
                                <PhotoboothPresentation dataTable={dataTable} values={values} touched={touched} errors={errors} handleChange={handleChange} setFieldValue={setFieldValue} setDataAddForm={setDataAddForm} dataAddForm={dataAddForm} handlePreviewSuccess={handlePreviewSuccess} handlePreviewError={handlePreviewError} fonts={fonts} setModal={setModal} applications={applications}></PhotoboothPresentation>
                            </Tab.Pane>
                        </Tab.Content>
                    </Tab.Container>
                </div>
            </Row>
        </>
    );
};

const GalleryPresentation = ({ dataTable, values, touched, errors, handleChange, setFieldValue, setDataAddForm, dataAddForm, handlePreviewSuccess, handlePreviewError, fonts, setModal, applications }) => {
    return (
        <>
            <Row className="mx-0 mb-3">
                <p className="text-start fw-bold mb-2">Grade de fotos</p>
                <Form.Group as={Col} sm={3} className='mb-3'>
                    <Form.Label>Linhas</Form.Label>
                    <Form.Control
                        type="number"
                        step={1}
                        name="json.apresentation.configs.rows"
                        value={values.json.apresentation.configs.rows}
                        onChange={(e) => {
                            handleChange(e);
                        }}
                        isInvalid={!!errors.json?.apresentation?.configs?.rows}
                    />
                    <Form.Control.Feedback type="valid"></Form.Control.Feedback>
                    <Form.Control.Feedback type="invalid">
                        {errors.json?.apresentation?.configs?.rows}
                    </Form.Control.Feedback>
                </Form.Group>
                <Form.Group as={Col} sm={3} className='mb-3'>
                    <Form.Label>Colunas</Form.Label>
                    <Form.Control
                        type="number"
                        step={1}
                        name="json.apresentation.configs.columns"
                        value={values.json.apresentation.configs.columns}
                        onChange={(e) => {
                            handleChange(e);
                        }}
                        isInvalid={!!errors.json?.apresentation?.configs?.columns}
                    />
                    <Form.Control.Feedback type="valid"></Form.Control.Feedback>
                    <Form.Control.Feedback type="invalid">
                        {errors.json?.apresentation?.configs?.columns}
                    </Form.Control.Feedback>
                </Form.Group>
                <Form.Group as={Col} sm={4} className='mb-3'>
                    <Form.Label>Tempo de rotação (milissegundos)</Form.Label>
                    <Form.Control
                        type="number"
                        step={1000}
                        name="json.apresentation.configs.transitionTime"
                        value={values.json.apresentation.configs.transitionTime}
                        onChange={(e) => {
                            handleChange(e);
                        }}
                        isInvalid={!!errors.json?.apresentation?.configs?.transitionTime}
                    />
                    <Form.Control.Feedback type="valid"></Form.Control.Feedback>
                    <Form.Control.Feedback type="invalid">
                        {errors.json?.apresentation?.configs?.transitionTime}
                    </Form.Control.Feedback>
                </Form.Group>
                <Form.Group as={Col} sm={3} className='mb-3'>
                    <Form.Label>Largura (px)</Form.Label>
                    <Form.Control
                        type="number"
                        name="json.apresentation.configs.width"
                        value={values.json.apresentation.configs.width}
                        onChange={(e) => {
                            handleChange(e);
                        }}
                        isInvalid={!!errors.json?.apresentation?.configs?.width}
                    />
                    <Form.Control.Feedback type="valid"></Form.Control.Feedback>
                    <Form.Control.Feedback type="invalid">
                        {errors.json?.apresentation?.configs?.width}
                    </Form.Control.Feedback>
                </Form.Group>
                <Form.Group as={Col} sm={3} className='mb-3'>
                    <Form.Label>Altura (px)</Form.Label>
                    <Form.Control
                        type="number"
                        name="json.apresentation.configs.height"
                        value={values.json.apresentation.configs.height}
                        onChange={(e) => {
                            handleChange(e);
                        }}
                        isInvalid={!!errors.json?.apresentation?.configs?.height}
                    />
                    <Form.Control.Feedback type="valid"></Form.Control.Feedback>
                    <Form.Control.Feedback type="invalid">
                        {errors.json?.apresentation?.configs?.height}
                    </Form.Control.Feedback>
                </Form.Group>
                <Form.Group as={Col} sm={3} className='mb-3'>
                    <Form.Label>Margem (rem)</Form.Label>
                    <Form.Control
                        type="number"
                        name="json.apresentation.configs.padding"
                        value={values.json.apresentation.configs.padding}
                        onChange={(e) => {
                            handleChange(e);
                        }}
                        isInvalid={!!errors.json?.apresentation?.configs?.padding}
                    />
                    <Form.Control.Feedback type="valid"></Form.Control.Feedback>
                    <Form.Control.Feedback type="invalid">
                        {errors.json?.apresentation?.configs?.padding}
                    </Form.Control.Feedback>
                </Form.Group>
            </Row>
            <Row className="mx-0 mb-3">
                <p className="text-start fw-bold mb-2">Foto de destaque</p>
                <Form.Group as={Col} sm={3} className='mb-3'>
                    <Form.Label>Largura (px)</Form.Label>
                    <Form.Control
                        type="number"
                        name="json.apresentation.configs.newImageWidth"
                        value={values.json.apresentation.configs.newImageWidth}
                        onChange={(e) => {
                            handleChange(e);
                        }}
                        isInvalid={!!errors.json?.apresentation?.configs?.newImageWidth}
                    />
                    <Form.Control.Feedback type="valid"></Form.Control.Feedback>
                    <Form.Control.Feedback type="invalid">
                        {errors.json?.apresentation?.configs?.newImageWidth}
                    </Form.Control.Feedback>
                </Form.Group>
                <Form.Group as={Col} sm={3} className='mb-3'>
                    <Form.Label>Altura (px)</Form.Label>
                    <Form.Control
                        type="number"
                        name="json.apresentation.configs.newImageHeight"
                        value={values.json.apresentation.configs.newImageHeight}
                        onChange={(e) => {
                            handleChange(e);
                        }}
                        isInvalid={!!errors.json?.apresentation?.configs?.newImageHeight}
                    />
                    <Form.Control.Feedback type="valid"></Form.Control.Feedback>
                    <Form.Control.Feedback type="invalid">
                        {errors.json?.apresentation?.configs?.newImageHeight}
                    </Form.Control.Feedback>
                </Form.Group>
                <Form.Group as={Col} sm={4} className='mb-3'>
                    <Form.Label>Tempo de destaque (milissegundos)</Form.Label>
                    <Form.Control
                        type="number"
                        step={1000}
                        name="json.apresentation.configs.newImageTotalTime"
                        value={values.json.apresentation.configs.newImageTotalTime}
                        onChange={(e) => {
                            handleChange(e);
                        }}
                        isInvalid={!!errors.json?.apresentation?.configs?.newImageTotalTime}
                    />
                    <Form.Control.Feedback type="valid"></Form.Control.Feedback>
                    <Form.Control.Feedback type="invalid">
                        {errors.json?.apresentation?.configs?.newImageTotalTime}
                    </Form.Control.Feedback>
                </Form.Group>
            </Row>
        </>
    )
}

const GalleryPresentationOverlay = ({ dataTable, values, touched, errors, handleChange, setFieldValue, setDataAddForm, dataAddForm, handlePreviewSuccess, handlePreviewError, fonts, setModal, applications }) => {
    return (
        <>
            <Row className="mx-0 mb-3">
                <p className="text-start fw-bold mb-2">Configurações</p>
                <Form.Group as={Col} sm="6" className="mb-3 d-flex align-items-end">
                    <Form.Check
                        checked={values.json.apresentation.configs.floatingAnimation !== false}
                        type="checkbox"
                        name="json.apresentation.configs.floatingAnimation"
                        label="Ativar animação flutuante (Imagens)"
                        onChange={(e) => {
                            setFieldValue(
                                "json.apresentation.configs.floatingAnimation",
                                e.target.checked
                            );
                        }}
                    />
                </Form.Group>
                <p className="text-start fw-bold mb-2">Grade de fotos</p>
                <Form.Group as={Col} sm={3} className='mb-3'>
                    <Form.Label>Linhas</Form.Label>
                    <Form.Control
                        type="number"
                        name="json.galleryOverlay.configs.galleryRows"
                        value={values.json.galleryOverlay.configs.galleryRows}
                        onChange={(e) => {
                            handleChange(e);
                        }}
                        isInvalid={!!errors.json?.galleryOverlay?.configs?.galleryRows}
                        min="1"
                        max="20"
                    />
                    <Form.Control.Feedback type="valid"></Form.Control.Feedback>
                    <Form.Control.Feedback type="invalid">
                        {errors.json?.galleryOverlay?.configs?.galleryRows}
                    </Form.Control.Feedback>
                </Form.Group>
                <Form.Group as={Col} sm={3} className='mb-3'>
                    <Form.Label>Colunas</Form.Label>
                    <Form.Control
                        type="number"
                        name="json.galleryOverlay.configs.galleryColumns"
                        value={values.json.galleryOverlay.configs.galleryColumns}
                        onChange={(e) => {
                            handleChange(e);
                        }}
                        isInvalid={!!errors.json?.galleryOverlay?.configs?.galleryColumns}
                        min="1"
                        max="20"
                    />
                    <Form.Control.Feedback type="valid"></Form.Control.Feedback>
                    <Form.Control.Feedback type="invalid">
                        {errors.json?.galleryOverlay?.configs?.galleryColumns}
                    </Form.Control.Feedback>
                </Form.Group>
                <Form.Group as={Col} sm={4} className='mb-3'>
                    <Form.Label>Tempo de rotação (milissegundos)</Form.Label>
                    <Form.Control
                        type="number"
                        step={1000}
                        name="json.galleryOverlay.configs.galleryTransitionTime"
                        value={values.json.galleryOverlay.configs.galleryTransitionTime}
                        onChange={(e) => {
                            handleChange(e);
                        }}
                        isInvalid={!!errors.json?.galleryOverlay?.configs?.galleryTransitionTime}
                    />
                    <Form.Control.Feedback type="valid"></Form.Control.Feedback>
                    <Form.Control.Feedback type="invalid">
                        {errors.json?.galleryOverlay?.configs?.galleryTransitionTime}
                    </Form.Control.Feedback>
                </Form.Group>
            </Row>
            <Row className="mx-0 mb-3">

                <Form.Group as={Col} sm={3} className='mb-3'>
                    <Form.Label>Largura (px)</Form.Label>
                    <Form.Control
                        type="number"
                        name="json.galleryOverlay.configs.imageWidth"
                        value={values.json.galleryOverlay.configs.imageWidth}
                        onChange={(e) => {
                            handleChange(e);
                        }}
                        isInvalid={!!errors.json?.galleryOverlay?.configs?.imageWidth}
                        min="50"
                        max="500"
                    />
                    <Form.Control.Feedback type="valid"></Form.Control.Feedback>
                    <Form.Control.Feedback type="invalid">
                        {errors.json?.galleryOverlay?.configs?.imageWidth}
                    </Form.Control.Feedback>
                </Form.Group>
                <Form.Group as={Col} sm={3} className='mb-3'>
                    <Form.Label>Altura (px)</Form.Label>
                    <Form.Control
                        type="number"
                        name="json.galleryOverlay.configs.imageHeight"
                        value={values.json.galleryOverlay.configs.imageHeight}
                        onChange={(e) => {
                            handleChange(e);
                        }}
                        isInvalid={!!errors.json?.galleryOverlay?.configs?.imageHeight}
                        min="50"
                        max="500"
                    />
                    <Form.Control.Feedback type="valid"></Form.Control.Feedback>
                    <Form.Control.Feedback type="invalid">
                        {errors.json?.galleryOverlay?.configs?.imageHeight}
                    </Form.Control.Feedback>
                </Form.Group>
            </Row>
            <Row className="mx-0 mb-3">
                <p className="text-start fw-bold mb-2">Container da Galeria</p>
                <Form.Group as={Col} sm={3} className='mb-3'>
                    <Form.Label>Posição Superior</Form.Label>
                    <Form.Control
                        type="text"
                        name="json.galleryOverlay.configs.galleryTop"
                        value={values.json.galleryOverlay.configs.galleryTop}
                        onChange={(e) => {
                            handleChange(e);
                        }}
                        isInvalid={!!errors.json?.galleryOverlay?.configs?.galleryTop}
                    />
                    <Form.Control.Feedback type="valid"></Form.Control.Feedback>
                    <Form.Control.Feedback type="invalid">
                        {errors.json?.galleryOverlay?.configs?.galleryTop}
                    </Form.Control.Feedback>
                </Form.Group>
                <Form.Group as={Col} sm={3} className='mb-3'>
                    <Form.Label>Posição Esquerda</Form.Label>
                    <Form.Control
                        type="text"
                        name="json.galleryOverlay.configs.galleryLeft"
                        value={values.json.galleryOverlay.configs.galleryLeft}
                        onChange={(e) => {
                            handleChange(e);
                        }}
                        isInvalid={!!errors.json?.galleryOverlay?.configs?.galleryLeft}
                    />
                    <Form.Control.Feedback type="valid"></Form.Control.Feedback>
                    <Form.Control.Feedback type="invalid">
                        {errors.json?.galleryOverlay?.configs?.galleryLeft}
                    </Form.Control.Feedback>
                </Form.Group>
                <Form.Group as={Col} sm={3} className='mb-3'>
                    <Form.Label>Posição Direita</Form.Label>
                    <Form.Control
                        type="text"
                        name="json.galleryOverlay.configs.galleryRight"
                        value={values.json.galleryOverlay.configs.galleryRight}
                        onChange={(e) => {
                            handleChange(e);
                        }}
                        isInvalid={!!errors.json?.galleryOverlay?.configs?.galleryRight}
                    />
                    <Form.Control.Feedback type="valid"></Form.Control.Feedback>
                    <Form.Control.Feedback type="invalid">
                        {errors.json?.galleryOverlay?.configs?.galleryRight}
                    </Form.Control.Feedback>
                </Form.Group>
                <Form.Group as={Col} sm={3} className='mb-3'>
                    <Form.Label>Posição Inferior</Form.Label>
                    <Form.Control
                        type="text"
                        name="json.galleryOverlay.configs.galleryBottom"
                        value={values.json.galleryOverlay.configs.galleryBottom}
                        onChange={(e) => {
                            handleChange(e);
                        }}
                        isInvalid={!!errors.json?.galleryOverlay?.configs?.galleryBottom}
                    />
                    <Form.Control.Feedback type="valid"></Form.Control.Feedback>
                    <Form.Control.Feedback type="invalid">
                        {errors.json?.galleryOverlay?.configs?.galleryBottom}
                    </Form.Control.Feedback>
                </Form.Group>
            </Row>
            <Row className="mx-0 mb-3">
                <p className="text-start fw-bold mb-2">Fundo da sobreposição</p>
                <Form.Group as={Col} md="6" className="mb-3">
                    <Form.Label>Tipo de fundo</Form.Label>
                    <Form.Select
                        name="json.galleryOverlay.background.type"
                        onChange={handleChange}
                        value={values.json.galleryOverlay?.background?.type}
                        isInvalid={!!errors?.json?.galleryOverlay?.background?.type}
                        isValid={touched?.json?.galleryOverlay?.background?.type && !errors?.json?.galleryOverlay?.background?.type}>
                        <option value={"image"}>Imagem</option>
                        <option value={"video"}>Vídeo</option>
                    </Form.Select>
                    <Form.Control.Feedback type="valid"></Form.Control.Feedback>
                    <Form.Control.Feedback type="invalid">{errors?.json?.galleryOverlay?.background?.type}</Form.Control.Feedback>
                </Form.Group>
            </Row>
            <Row className="mb-3 mx-0">
                {(() => {
                    switch (values.json.galleryOverlay?.background?.type) {
                        case 'image':
                            return (
                                <Form.Group as={Col} md="6" className="mb-3">
                                    <Card className="mb-0">
                                        <Card.Body>
                                            <Form.Control
                                                type="hidden"
                                                name="json.galleryOverlay.background.value"
                                                value={values.json.galleryOverlay?.background?.value}
                                                onChange={(e) => {
                                                    setFieldValue("json.galleryOverlay.background.value", e.target.value);
                                                }}
                                                isInvalid={!!errors?.json?.galleryOverlay?.background?.value}
                                                isValid={touched?.json?.galleryOverlay?.background?.value && !errors?.json?.galleryOverlay?.background?.value}
                                            />
                                            <div className="imagesUpload">
                                                <img
                                                    className={`h-100 img-thumbnail ${!!errors?.json?.galleryOverlay?.background?.value ? "is-invalid" : ""}`}
                                                    src={
                                                        values.json.galleryOverlay?.background?.value !== "" && values.json.galleryOverlay?.background?.value !== null
                                                            ? domainConfig.imageServer +
                                                            "/customizacao/" +
                                                            values.customizacao_id +
                                                            "/" +
                                                            values.json.galleryOverlay?.background?.value
                                                            : domainConfig.imageServer + "/empty-background.png"
                                                    }
                                                    alt="Imagem preview"
                                                />
                                                <Form.Control.Feedback type="valid"></Form.Control.Feedback>
                                                <Form.Control.Feedback type="invalid">
                                                    {errors?.json?.galleryOverlay?.background?.value}
                                                </Form.Control.Feedback>
                                            </div>
                                            <DropzoneComponent
                                                className="mt-3 dropzoneComp"
                                                type="image"
                                                handlePreviewSuccess={handlePreviewSuccess}
                                                handlePreviewError={handlePreviewError}
                                                setFieldValue={setFieldValue}
                                                folder="customizacao"
                                                id={values.customizacao_id}
                                                field="json.galleryOverlay.background.value"
                                            />
                                        </Card.Body>
                                    </Card>
                                </Form.Group>
                            );
                        case 'video':
                            return (
                                <Form.Group as={Col} md="6" className="mb-3">
                                    <Card className="mb-0">
                                        <Card.Body>
                                            <Form.Control
                                                type="hidden"
                                                name="json.galleryOverlay.background.value"
                                                value={values.json.galleryOverlay?.background?.value}
                                                onChange={(e) => {
                                                    setFieldValue("json.galleryOverlay.background.value", e.target.value);
                                                }}
                                                isInvalid={!!errors?.json?.galleryOverlay?.background?.value}
                                                isValid={touched?.json?.galleryOverlay?.background?.value && !errors?.json?.galleryOverlay?.background?.value}
                                            />
                                            <div className="imagesUpload">
                                                <video
                                                    className={`h-100 img-thumbnail ${!!errors?.json?.galleryOverlay?.background?.value ? "is-invalid" : ""}`}
                                                    src={
                                                        values.json.galleryOverlay?.background?.value !== "" && values.json.galleryOverlay?.background?.value !== null
                                                            ? domainConfig.imageServer +
                                                            "/customizacao/" +
                                                            values.customizacao_id +
                                                            "/" +
                                                            values.json.galleryOverlay?.background?.value
                                                            : domainConfig.imageServer + "/empty-background.png"
                                                    }
                                                    controls={true}
                                                />
                                                <Form.Control.Feedback type="valid"></Form.Control.Feedback>
                                                <Form.Control.Feedback type="invalid">
                                                    {errors?.json?.galleryOverlay?.background?.value}
                                                </Form.Control.Feedback>
                                            </div>
                                            <DropzoneComponent
                                                className="mt-3 dropzoneComp"
                                                type="video"
                                                handlePreviewSuccess={handlePreviewSuccess}
                                                handlePreviewError={handlePreviewError}
                                                setFieldValue={setFieldValue}
                                                folder="customizacao"
                                                id={values.customizacao_id}
                                                field="json.galleryOverlay.background.value"
                                            />
                                        </Card.Body>
                                    </Card>
                                </Form.Group>
                            );
                        default:
                            return ("");
                    }
                })()}
            </Row>
        </>)
}

const PhotoboothPresentation = ({ dataTable, values, touched, errors, handleChange, setFieldValue, setDataAddForm, dataAddForm, handlePreviewSuccess, handlePreviewError, fonts, setModal, applications }) => {
    const isFrameEnabled = (
        values.json.apresentation?.configs?.enableFrame ??
        values.json.apresentation?.frame?.status
    ) === true;

    return (
        <>
            <Row className="mx-0 mb-3">
                <div className="d-flex text-start">
                    <Form.Group sm={6} className="mb-3">
                        <Form.Check
                            checked={values.json.apresentation?.configs?.showLastUser === true ? true : false}
                            type="checkbox"
                            name="json.apresentation.configs.showLastUser"
                            label="Adicionar botão para mostrar último QRCode gerado"
                            onChange={(e) => {
                                setFieldValue(
                                    "json.apresentation.configs.showLastUser",
                                    e.target.checked
                                );
                            }}
                        />
                        <Form.Check
                            checked={isFrameEnabled}
                            type="checkbox"
                            name="json.apresentation.configs.enableFrame"
                            label="Ativar moldura"
                            onChange={(e) => {
                                setFieldValue('json.apresentation.configs.enableFrame', e.target.checked);
                                setFieldValue('json.apresentation.frame.status', e.target.checked);
                                if (!e.target.checked) {
                                    setFieldValue('json.apresentation.frame.value', '');
                                    setFieldValue('json.apresentation.frameHorizontal', '');
                                }
                            }}
                        />
                    </Form.Group>
                </div>
                <p className="text-start fw-bold mt-3 mb-2">Formulário</p>
                <Form.Group sm={12} className="mb-3">
                    <Form.Check
                        className="mode"
                        checked={
                            values.json.apresentation?.configs?.form === false ||
                            values.json.apresentation?.configs?.form === 0 ||
                            values.json.apresentation?.configs?.form === "0" ||
                            values.json.apresentation?.configs?.form === undefined
                        }
                        type="checkbox"
                        name="json.apresentation.configs.form"
                        onChange={() => {
                            setFieldValue('json.apresentation.configs.form', false);
                        }}
                        label="Sem coleta"
                        isInvalid={!!errors.json?.apresentation?.configs?.form}
                        feedback={errors.json?.apresentation?.configs?.form}
                        feedbackType="invalid"
                    />
                    <Form.Check
                        className="mode"
                        checked={Number(values.json.apresentation?.configs?.form) === 1}
                        type="checkbox"
                        name="json.apresentation.configs.form"
                        onChange={() => {
                            setFieldValue('json.apresentation.configs.form', 1);
                        }}
                        label="Durante o processo de captura"
                        isInvalid={!!errors.json?.apresentation?.configs?.form}
                        feedback={errors.json?.apresentation?.configs?.form}
                        feedbackType="invalid"
                    />
                </Form.Group>
                <p className="text-start fw-bold mb-2"><FontAwesomeIcon
                    icon={faTabletScreenButton}
                    className="me-1"
                />Tela da câmera</p>
                <Form.Group
                    as={Col} sm={8} className='mb-3'
                >
                    <Form.Label>Texto do botão</Form.Label>
                    <Form.Control
                        type="text"
                        name="json.apresentation.firstPage.buttonText"
                        value={values.json.apresentation.firstPage.buttonText}
                        onChange={handleChange}
                        isInvalid={!!errors?.json?.apresentation?.firstPage?.buttonText}
                        isValid={touched?.json?.apresentation?.firstPage?.buttonText && !errors.json?.apresentation?.firstPage?.buttonText}
                    />
                    <Form.Control.Feedback type="valid"></Form.Control.Feedback>
                    <Form.Control.Feedback type="invalid">
                        {errors?.json?.apresentation?.firstPage?.buttonText}
                    </Form.Control.Feedback>
                </Form.Group>
                <Form.Group as={Col} sm={4} className='mb-3'>
                    <Form.Label>Posição do container (%)</Form.Label>
                    <Form.Control
                        type="number"
                        name="json.apresentation.configs.buttonPosition"
                        value={values.json.apresentation.configs.buttonPosition}
                        onChange={(e) => {
                            handleChange(e);
                        }}
                        isInvalid={!!errors.json?.apresentation?.configs?.buttonPosition}
                    />
                    <Form.Control.Feedback type="valid"></Form.Control.Feedback>
                    <Form.Control.Feedback type="invalid">
                        {errors.json?.apresentation?.configs?.buttonPosition}
                    </Form.Control.Feedback>
                </Form.Group>
            </Row>
            <Row className="mx-0 mb-3">
                <p className="text-start fw-bold mb-2">Tela do formulário</p>
                <Form.Group className="mb-3" as={Col} sm="12">
                    <Form.Label>Título</Form.Label>
                    <Form.Control
                        as={'textarea'}
                        name="json.apresentation.register.title"
                        value={values.json.apresentation?.register?.title || ''}
                        onChange={handleChange}
                        isInvalid={!!errors?.json?.apresentation?.register?.title}
                        isValid={touched?.json?.apresentation?.register?.title && !errors.json?.apresentation?.register?.title}
                    />
                    <Form.Control.Feedback type="valid"></Form.Control.Feedback>
                    <Form.Control.Feedback type="invalid">
                        {errors?.json?.apresentation?.register?.title}
                    </Form.Control.Feedback>
                </Form.Group>
            </Row>
            <Row className="mx-0 mb-3">
                <p className="text-start fw-bold mb-2"><FontAwesomeIcon
                    icon={faTabletScreenButton}
                    className="me-1"
                />Tela do resultado</p>
                <Form.Group
                    className="mb-3"
                    as={Col}
                    sm="12">
                    <Form.Label>Título</Form.Label>
                    <Form.Control
                        as={'textarea'}
                        name="json.apresentation.endPage.title"
                        value={values.json.apresentation?.endPage?.title || ''}
                        onChange={handleChange}
                        isInvalid={!!errors?.json?.apresentation?.endPage?.title}
                        isValid={touched?.json?.apresentation?.endPage?.title && !errors.json?.apresentation?.endPage?.title}
                    />
                    <Form.Control.Feedback type="valid"></Form.Control.Feedback>
                    <Form.Control.Feedback type="invalid">
                        {errors?.json?.apresentation?.endPage?.title}
                    </Form.Control.Feedback>
                </Form.Group>
                <Form.Group
                    className="mb-3"
                    as={Col}
                    sm="12">
                    <Form.Label>Descrição</Form.Label>
                    <Form.Control
                        as={'textarea'}
                        name="json.apresentation.endPage.description"
                        value={values.json.apresentation?.endPage?.description || ''}
                        onChange={handleChange}
                        isInvalid={!!errors?.json?.apresentation?.endPage?.description}
                        isValid={touched?.json?.apresentation?.endPage?.description && !errors.json?.apresentation?.endPage?.description}
                    />
                    <Form.Control.Feedback type="valid"></Form.Control.Feedback>
                    <Form.Control.Feedback type="invalid">
                        {errors?.json?.apresentation?.endPage?.description}
                    </Form.Control.Feedback>
                </Form.Group>
                <Form.Group
                    className="mb-3"
                    as={Col}
                    sm="8">
                    <Form.Label>Texto do botão</Form.Label>
                    <Form.Control
                        type="text"
                        name="json.apresentation.endPage.buttonText"
                        value={values.json.apresentation?.endPage?.buttonText || ''}
                        onChange={handleChange}
                        isInvalid={!!errors?.json?.apresentation?.endPage?.buttonText}
                        isValid={touched?.json?.apresentation?.endPage?.buttonText && !errors.json?.apresentation?.endPage?.buttonText}
                    />
                    <Form.Control.Feedback type="valid"></Form.Control.Feedback>
                    <Form.Control.Feedback type="invalid">
                        {errors?.json?.apresentation?.endPage?.buttonText}
                    </Form.Control.Feedback>
                </Form.Group>
                <Form.Group as={Col} sm={4} className='mb-3'>
                    <Form.Label>Posição do container (%)</Form.Label>
                    <Form.Control
                        type="number"
                        name="json.apresentation.configs.buttonPositionEnd"
                        value={values.json.apresentation.configs.buttonPositionEnd}
                        onChange={(e) => {
                            handleChange(e);
                        }}
                        isInvalid={!!errors.json?.apresentation?.configs?.buttonPositionEnd}
                    />
                    <Form.Control.Feedback type="valid"></Form.Control.Feedback>
                    <Form.Control.Feedback type="invalid">
                        {errors.json?.apresentation?.configs?.buttonPositionEnd}
                    </Form.Control.Feedback>
                </Form.Group>
                <Form.Group as={Col} sm={6} className='mb-3'>
                    <Form.Label>Finalizar fluxo automaticamente (milissegundos)</Form.Label>
                    <Form.Control
                        type="number"
                        step={1000}
                        name="json.apresentation.configs.qrcodeEndPage"
                        value={values.json.apresentation?.configs?.qrcodeEndPage || ''}
                        onChange={(e) => {
                            handleChange(e);
                        }}
                        isInvalid={!!errors.json?.apresentation?.configs?.qrcodeEndPage}
                    />
                    <Form.Control.Feedback type="valid"></Form.Control.Feedback>
                    <Form.Control.Feedback type="invalid">
                        {errors.json?.apresentation?.configs?.qrcodeEndPage}
                    </Form.Control.Feedback>
                </Form.Group>
            </Row>


            {isFrameEnabled && (
                <Row className="mx-0 mb-3">
                    <p className="text-start fw-bold mb-2">Media <span className="small">(Tamanho máximo: 10mb)</span></p>
                    <Form.Group
                        as={Col}
                        md="6" className="mb-3">
                        <Card className="mb-3">
                            <Card.Body>
                                <div className="text-center mb-3">
                                    <div className="d-flex justify-content-center mb-3">
                                        <Form.Label>Frame vertical</Form.Label>
                                    </div>
                                </div>
                                <Form.Control
                                    type="hidden"
                                    name="json.apresentation.frame.value"
                                    value={values.json.apresentation.frame.value}
                                    onChange={handleChange}
                                    isInvalid={!!errors?.json?.apresentation?.frame?.value}
                                    isValid={touched?.json?.apresentation?.frame?.value && !errors?.json?.apresentation?.frame?.value}
                                />
                                <div className="imagesUpload">
                                    <img
                                        className={`h-100 img-thumbnail ${!!errors?.json?.apresentation?.frame?.value ? "is-invalid" : ""}`}
                                        src={
                                            values.json.apresentation.frame.value !== "" && values.json.apresentation.frame.value !== null
                                                ? domainConfig.imageServer +
                                                "/customizacao/" +
                                                values.customizacao_id +
                                                "/" +
                                                values.json.apresentation.frame.value
                                                : domainConfig.imageServer + "/empty-background.png"
                                        }
                                        alt="ImagemBase"
                                    />
                                    <Form.Control.Feedback type="valid"></Form.Control.Feedback>
                                    <Form.Control.Feedback type="invalid">
                                        {errors?.json?.apresentation?.frame?.value}
                                    </Form.Control.Feedback>
                                </div>
                                <DropzoneComponent
                                    className="mt-3 dropzoneComp"
                                    type="image"
                                    handlePreviewSuccess={handlePreviewSuccess}
                                    handlePreviewError={handlePreviewError}
                                    setFieldValue={setFieldValue}
                                    folder="customizacao"
                                    id={values.customizacao_id}
                                    field="json.apresentation.frame.value"
                                />
                            </Card.Body>
                        </Card>
                    </Form.Group>
                    <Form.Group
                        as={Col}
                        md="6" className="mb-3">
                        <Card className="mb-3">
                            <Card.Body>
                                <div className="text-center mb-3">
                                    <div className="d-flex justify-content-center mb-3">
                                        <Form.Label>Frame horizontal</Form.Label>
                                    </div>
                                </div>
                                <Form.Control
                                    type="hidden"
                                    name="json.apresentation.frameHorizontal"
                                    value={values.json.apresentation.frameHorizontal || ''}
                                    onChange={handleChange}
                                />
                                <div className="imagesUpload">
                                    <img
                                        className="h-100 img-thumbnail"
                                        src={
                                            values.json.apresentation.frameHorizontal
                                                ? domainConfig.imageServer +
                                                "/customizacao/" +
                                                values.customizacao_id +
                                                "/" +
                                                values.json.apresentation.frameHorizontal
                                                : domainConfig.imageServer + "/empty-background.png"
                                        }
                                        alt="Frame horizontal"
                                    />
                                </div>
                                <DropzoneComponent
                                    className="mt-3 dropzoneComp"
                                    type="image"
                                    handlePreviewSuccess={handlePreviewSuccess}
                                    handlePreviewError={handlePreviewError}
                                    setFieldValue={setFieldValue}
                                    folder="customizacao"
                                    id={values.customizacao_id}
                                    field="json.apresentation.frameHorizontal"
                                />
                            </Card.Body>
                        </Card>
                    </Form.Group>
                </Row>
            )}
        </>)
}

export default Gallery
